import { StackScreenProps } from "@react-navigation/stack";
import { ApiKeyVaultRootParamList } from "../../../../types";
import { useMutation, useQuery } from "@apollo/client";
import {
  MUTATION_DELETE_USER_API_KEY,
  QUERY_USER_API_KEYS,
} from "../../../../utilities/user-api-keys";
import {
  FlatActionList,
  IconButton,
  Page,
  Button,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import { useTranslation } from "../../../../utilities/i18n";
import { View } from "react-native-ui-lib";
import {
  Success,
  UserApiKey,
} from "@towersystems/roam-common/lib/generated-types";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
} from "@towersystems/roam-common/lib/shared-utils";
import colors from "react-native-ui-lib/src/style/colors";
import { Feather } from "@expo/vector-icons";
import React from "react";
import { useFocusEffect } from "@react-navigation/native";

export interface ApiKeyVaultListScreenProps
  extends StackScreenProps<ApiKeyVaultRootParamList, "ApiKeyVaultListScreen"> {}

export const ApiKeyVaultListScreen = ({
  navigation,
}: ApiKeyVaultListScreenProps) => {
  const { t } = useTranslation("api_key_vault");

  const { loading, data, called, refetch } = useQuery<{
    userApiKeys: { items: UserApiKey[] };
  }>(QUERY_USER_API_KEYS);

  const flashMessagesContext = useFlashMessages();

  const [m] = useMutation<{
    deleteUserApiKey: Success;
  }>(MUTATION_DELETE_USER_API_KEY);

  useFocusEffect(
    React.useCallback(() => {
      if (called) {
        refetch();
      }
    }, [called])
  );

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        return (
          <Button
            label={t("button.add_api_key")}
            isAction
            onPress={() =>
              navigation.navigate("CreateNewApiVaultKeyRootScreen", {
                screen: "CreateNewApiVaultKeyScreen",
              })
            }
            size="compact"
          ></Button>
        );
      },
    });
  }, []);

  const handleDelete = async (id: string) => {
    m({
      variables: {
        id,
      },
    })
      .then((result) => {
        if (result?.data?.deleteUserApiKey) {
          flashMessagesContext.showMessage({
            message: "API Key Deleted",
            severity: "success",
          });
          refetch();
        } else {
          throw new Error("Could not delete API Key");
        }
      })
      .catch((err) => {
        console.log(err);
        flashMessagesContext.showMessage({
          message: err?.message ?? "Could not delete API Key",
          severity: "error",
        });
      });
  };

  const hasLoaded = called && !loading;

  const items = data?.userApiKeys.items || [];

  const listMarkup =
    hasLoaded && items && items.length > 0 ? (
      <View>
        <FlatActionList
          items={items.map((i) => ({
            key: i.id,
            title: i.keyType,
            subTitle: `Created ${formatDateToCalendar(
              i.createdAt,
              fullDateTimeFormat
            )}`,
            componentRight() {
              return (
                <View style={{ alignItems: "flex-end" }}>
                  <IconButton
                    Icon={
                      <Feather
                        name="trash-2"
                        size={20}
                        color={colors.destructive}
                      />
                    }
                    onPress={() => handleDelete(i.id)}
                    style={{
                      borderWidth: 0,
                      padding: 0,
                      minWidth: 0,
                    }}
                  />
                </View>
              );
            },
          }))}
        />
      </View>
    ) : null;

  return (
    <Page
      title={t("title.vault_settings_list")}
      secondaryTitle={t("title.vault_settings_list_secondary")}
    >
      {listMarkup}
    </Page>
  );
};
